import React, { useState, useEffect } from 'react';
import { ataItemsApi } from '../api';
import { toast } from 'sonner';
import {
  NotebookText,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  ListChecks,
} from 'lucide-react';

// Todas as categorias, usada para exibir o rótulo de itens já cadastrados
// (inclui "tendencia" para não quebrar itens antigos criados com essa categoria)
const ATA_ITEM_CATEGORIES = [
  { value: 'pauta', label: 'Pauta' },
  { value: 'acao', label: 'Ação' },
  { value: 'tendencia', label: 'Tendência' },
];

// Categorias selecionáveis ao criar/editar/filtrar: "Tendência" foi removida das opções
const ATA_ITEM_CATEGORY_OPTIONS = ATA_ITEM_CATEGORIES.filter((c) => c.value !== 'tendencia');

const AtaItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    category: 'pauta',
    is_active: true,
  });

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      const response = await ataItemsApi.list(params);
      setItems(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar itens de ATA');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        code: item.code || '',
        description: item.description || '',
        category: item.category || 'pauta',
        is_active: item.is_active !== false,
      });
    } else {
      setEditingItem(null);
      setFormData({ code: '', description: '', category: 'pauta', is_active: true });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error('A descrição é obrigatória');
      return;
    }

    setSaving(true);

    const cleanedData = {
      ...formData,
      code: formData.code.trim() || null,
      description: formData.description.trim(),
    };

    try {
      if (editingItem) {
        await ataItemsApi.update(editingItem.id, cleanedData);
        toast.success('Item de ATA atualizado com sucesso!');
      } else {
        await ataItemsApi.create(cleanedData);
        toast.success('Item de ATA cadastrado com sucesso!');
      }
      handleCloseModal();
      loadItems();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ')
        : detail || 'Erro ao salvar item';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Desativar o item "${item.code}"?`)) return;

    try {
      await ataItemsApi.delete(item.id);
      toast.success('Item de ATA desativado com sucesso!');
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar item');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await ataItemsApi.reactivate(item.id);
      toast.success('Item de ATA reativado com sucesso!');
      loadItems();
    } catch (error) {
      toast.error('Erro ao reativar item');
    }
  };

  const filteredItems = items.filter((i) =>
    i.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeItems = filteredItems.filter((i) => i.is_active);
  const inactiveItems = filteredItems.filter((i) => !i.is_active);

  return (
    <div className="space-y-6" data-testid="ata-items-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <NotebookText className="text-green-400" />
            Itens de ATA
          </h2>
          <p className="text-gray-400 mt-1">Cadastre itens reutilizáveis de Pauta e Ações para as atas (Alta Direção e Ata de Reunião)</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary" data-testid="new-ata-item-btn">
          <Plus size={18} />
          Novo Item
        </button>
      </div>

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por código ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="input px-3 w-full sm:w-60"
          data-testid="category-filter"
        >
          <option value="">Todas as Categorias</option>
          {ATA_ITEM_CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <ListChecks className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Itens</p>
              <p className="text-2xl font-bold text-blue-400">{filteredItems.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-400">{activeItems.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="text-red-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inativos</p>
              <p className="text-2xl font-bold text-red-400">{inactiveItems.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Nenhum item de ATA encontrado
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-800/50" data-testid={`ata-item-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-green-400">{item.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white">{item.description}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.category === 'acao' ? 'bg-purple-500/20 text-purple-300'
                        : item.category === 'tendencia' ? 'bg-amber-500/20 text-amber-300'
                        : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {ATA_ITEM_CATEGORIES.find((c) => c.value === item.category)?.label || item.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.is_active ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                          <CheckCircle size={12} />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                          <XCircle size={12} />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenModal(item)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        {item.is_active ? (
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                            title="Desativar"
                          >
                            <Trash2 size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(item)}
                            className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                            title="Reativar"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingItem ? 'Editar Item de ATA' : 'Novo Item de ATA'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="ITATA-0001 (auto)"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Categoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input w-full"
                  >
                    {ATA_ITEM_CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Descrição <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Texto padrão do item de pauta/ação..."
                  className="input w-full"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Item Ativo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtaItems;
